// features/admin/userlistApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  status: string;
  member_type: string;
  subscription_date: string | null;
  expiry_date: string | null;
  otp?: string | number;
}

export interface GrantVendorAccessResponse {
  error: boolean;
  message: string;
  vendor_id?: number;
  user_email?: string;
  temp_password?: string;
}

export interface SendCouponResponse {
  error: boolean;
  message: string;
  sent_users: number;
  already_sent_users: number;
}


export const userListApi = createApi({
  reducerPath: 'userListApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set(
        'authkey',
        '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
      );
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['UserList'],
  endpoints: (build) => ({
    // ✅ List Users with proper response handling
    listUsers: build.query<
      { 
        data: User[]; 
        total: number; 
        error: boolean; 
        message: string 
      }, 
      { filter_type?: number }
    >({
      query: (params) => {
        const requestBody = {
          filter_type: params?.filter_type ?? 0
        };
        console.log('ListUsers request body:', requestBody);
        return {
          url: 'admin/listUsers',
          method: 'POST',
          body: requestBody,
        };
      },
      providesTags: ['UserList'],
      transformResponse: (response: any) => {
        console.log('ListUsers API raw response:', response);
        
        // Handle different response structures
        let users: User[] = [];
        let total = 0;
        
        if (response?.error === false && response?.data && Array.isArray(response.data)) {
          users = response.data;
          total = response.total || response.count || users.length;
        } else if (Array.isArray(response)) {
          users = response;
          total = response.length;
        } else if (response?.data && Array.isArray(response.data)) {
          users = response.data;
          total = response.data.length;
        } else if (response?.users && Array.isArray(response.users)) {
          users = response.users;
          total = response.users.length;
        }
        
        // Transform users to ensure consistent structure
        const transformedUsers = users.map((user: any) => ({
          user_id: user.user_id || user.id || 0,
          first_name: user.first_name || user.firstName || '',
          last_name: user.last_name || user.lastName || '',
          user_email: user.user_email || user.email || '',
          user_contact: user.user_contact || user.contact || user.phone || '',
          status: user.status || 'active',
          member_type: user.member_type || user.membership_type || 'none',
          subscription_date: user.subscription_date || null,
          expiry_date: user.expiry_date || null,
          otp: user.otp || '',
        }));
        
        console.log('Transformed users:', transformedUsers);
        
        return {
          data: transformedUsers,
          total: total,
          error: response?.error || false,
          message: response?.message || 'Users loaded successfully',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('ListUsers API error:', response);
        return {
          data: [],
          total: 0,
          error: true,
          message: response.data?.message || response.error || 'Failed to load users',
        };
      },
    }),

    // ✅ Send Coupon
    sendCoupon: build.mutation<
      SendCouponResponse,
      { coupon_id: number; user_id: number }
    >({
      query: (body) => {
        console.log('SendCoupon request body:', body);
        return {
          url: 'user/send-coupon',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['UserList'],
    }),

    // ✅ Grant Vendor Access — turns an existing app user into a vendor who
    // can log in from the Vendor Login page immediately (no OTP step, since
    // an admin is directly granting this).
    grantVendorAccess: build.mutation<GrantVendorAccessResponse, { user_id: number }>({
      query: (body) => ({
        url: 'admin/grantVendorAccess',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UserList'],
    }),
  }),
});

export const {
  useListUsersQuery,
  useSendCouponMutation,
  useGrantVendorAccessMutation,
} = userListApi;